import { Content } from '../../ui/Grid';
import React, { Factory, Fragment, ReactElement } from 'react';
import * as uuid from 'uuid';
import {
  ViewLayout,
  RowLayout,
  ColLayout,
  Feature as FeatureNode,
  TreeNodeTypes,
} from './layoutTree';

export type LayoutNodes = ViewLayout | RowLayout | ColLayout | FeatureNode;

export interface NodeRendererRenderProp<T extends LayoutNodes> {
  node: Readonly<Omit<T & { children: never }, 'children'>>;
}

export interface LayoutNodeRendererRenderPropOptions<T extends LayoutNodes>
  extends NodeRendererRenderProp<T> {
  children: ReactElement[];
}

export interface FeatureNodeRendererRenderPropOptions<T extends LayoutNodes>
  extends NodeRendererRenderProp<T> {
  next?: () => void;
}

export interface LayoutNodeRenderers {
  view: Factory<NonNullable<LayoutNodeRendererRenderPropOptions<ViewLayout>>>;
  row: Factory<NonNullable<LayoutNodeRendererRenderPropOptions<RowLayout>>>;
  col: Factory<NonNullable<LayoutNodeRendererRenderPropOptions<ColLayout>>>;
  feature: Factory<
    NonNullable<FeatureNodeRendererRenderPropOptions<FeatureNode>>
  >;
}

export interface RenderLayoutOptions extends LayoutNodeRenderers {
  node: LayoutNodes;
}

// NOTE: Used for exhaustive matching.
const emptyRender = (_nodeType: never, key: string) => <Fragment key={key} />;

const renderLayout: Factory<RenderLayoutOptions> = (options) => {
  if (!options) return <Fragment />;

  const { node, view, row, col, feature } = options;

  const key = node?.id ?? uuid.v4();

  if (node.type === TreeNodeTypes.FEATURE) {
    return (
      <Content key={key}>
        {feature({ node: Object.freeze({ ...node, children: null }) })}
      </Content>
    );
  }

  const childrenMapCallback = <T extends LayoutNodes>(child: T) =>
    renderLayout({
      node: child,
      view,
      row,
      col,
      feature,
    });

  if (node.type === TreeNodeTypes.VIEW) {
    return (
      <Fragment key={key}>
        {view({
          node: Object.freeze({ ...node, children: null }),
          children: node.children.map(childrenMapCallback),
        })}
      </Fragment>
    );
  }

  if (node.type === TreeNodeTypes.ROW) {
    return (
      <Fragment key={key}>
        {row({
          node: Object.freeze({ ...node, children: null }),
          children: node.children.map(childrenMapCallback),
        })}
      </Fragment>
    );
  }

  if (node.type === TreeNodeTypes.COL) {
    return (
      <Fragment key={key}>
        {col({
          node: Object.freeze({ ...node, children: null }),
          children: node.children.map(childrenMapCallback),
        })}
      </Fragment>
    );
  }

  // NOTE: Exhaustive matching.
  const { type } = node;

  return emptyRender(type, key);
};

export default renderLayout;
